import { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from '../../redux/ReduxHooks';
import * as monaco from 'monaco-editor';
import { combineSnippetSupportingCode, insertSnippetAtLine, uniquifyVariableName } from '../../Components/CodeEditor/utils/snippetInsertion';
import type { PendingActionDrop } from '../../Components/CodeEditor/components/ActionArgumentsDialog';
import type { CodeSnippetImport } from '../../Components/CodeEditor/types';
import type { PendingDeviceSnippetPortDrop } from '../../utils/deviceSnippetPortDrop';
import type { Path } from '../../Components/PathBuilder/pathBuilderTypes';

interface UseEditorDialogsParams {
  standaloneEditorRef: MutableRefObject<monaco.editor.IStandaloneCodeEditor | null>;
  debouncedSync: (code: string) => void;
  onSnippetInserted?: (title: string) => void;
}

interface PendingPathDrop {
  code: string;
  targetLine: number;
  imports?: CodeSnippetImport[];
  snippetTitle?: string;
}

export function useEditorDialogs({ standaloneEditorRef, debouncedSync, onSnippetInserted }: UseEditorDialogsParams) {
  const project = useAppSelector((state) => state.project.loadedProject);
  const postDialogFocusTimerRef = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);

  const [pathDialogOpen, setPathDialogOpen] = useState(false);
  const [selectedPathName, setSelectedPathName] = useState('');
  const [pendingPathDrop, setPendingPathDrop] = useState<PendingPathDrop | null>(null);
  const [noPathsDialogOpen, setNoPathsDialogOpen] = useState(false);

  const [actionArgsDialogOpen, setActionArgsDialogOpen] = useState(false);
  const [pendingActionDrop, setPendingActionDrop] = useState<PendingActionDrop | null>(null);

  const [deviceSnippetPortDialogOpen, setDeviceSnippetPortDialogOpen] = useState(false);
  const [pendingDeviceSnippetPortDrop, setPendingDeviceSnippetPortDrop] = useState<(PendingDeviceSnippetPortDrop & { targetLine: number }) | null>(
    null
  );
  const [duplicateDriveBaseDialogOpen, setDuplicateDriveBaseDialogOpen] = useState(false);
  const pathOptions = useMemo<Array<{ id: string; name: string }>>(
    () => (project?.paths ?? []).map((p: Path) => ({ id: p.id, name: p.name })),
    [project?.paths]
  );

  useEffect(
    () => () => {
      if (postDialogFocusTimerRef.current != null) {
        globalThis.clearTimeout(postDialogFocusTimerRef.current);
      }
    },
    []
  );

  const focusEditorAfterDialogClose = (editor: monaco.editor.IStandaloneCodeEditor) => {
    if (postDialogFocusTimerRef.current != null) {
      globalThis.clearTimeout(postDialogFocusTimerRef.current);
    }
    postDialogFocusTimerRef.current = globalThis.setTimeout(() => {
      editor.focus();
      postDialogFocusTimerRef.current = null;
    }, 0);
  };

  const handlePathDialogClose = () => {
    setPathDialogOpen(false);
    setPendingPathDrop(null);
  };

  const handlePathDialogConfirm = () => {
    const editorInst = standaloneEditorRef.current;
    if (!editorInst || !pendingPathDrop || !selectedPathName) return;
    const model = editorInst.getModel();
    if (!model) return;

    let filled = pendingPathDrop.code;
    if (/\bpath\s*=\s*(?=[,)]|$)/.test(filled)) {
      filled = filled.replace(/\bpath\s*=\s*(?=[,)]|$)/g, `path="${selectedPathName}"`);
    } else {
      filled = filled.replace(/\bname\s*=\s*(?=[,)]|$)/g, `name="${selectedPathName}"`);
    }

    insertSnippetAtLine({
      editor: editorInst,
      textToInsert: filled,
      targetLine: pendingPathDrop.targetLine,
      imports: pendingPathDrop.imports,
    });

    try {
      debouncedSync(model.getValue());
    } catch {
      // Ignore sync errors
    }

    if (pendingPathDrop.snippetTitle) {
      onSnippetInserted?.(pendingPathDrop.snippetTitle);
    }

    setPathDialogOpen(false);
    setPendingPathDrop(null);
    setSelectedPathName('');
    focusEditorAfterDialogClose(editorInst);
  };

  const handleNoPathsDialogClose = () => {
    setNoPathsDialogOpen(false);
  };

  const requestPathDrop = (code: string, targetLine: number, imports?: CodeSnippetImport[], snippetTitle?: string) => {
    setPendingPathDrop({ code, targetLine, imports, snippetTitle });
    setSelectedPathName('');
    setPathDialogOpen(true);
  };

  const requestNoPathsDialog = () => {
    setNoPathsDialogOpen(true);
    setPendingPathDrop(null);
  };

  const insertSnippetWithUniquify = (
    code: string,
    targetLine: number,
    imports?: CodeSnippetImport[],
    insertText?: string,
    supportingCode?: string
  ): boolean => {
    const editorInst = standaloneEditorRef.current;
    if (!editorInst) return false;
    const model = editorInst.getModel();
    if (!model) return false;

    const textToInsert = combineSnippetSupportingCode(uniquifyVariableName(code, model), supportingCode);
    const snippetTextToInsert = insertText ? combineSnippetSupportingCode(uniquifyVariableName(insertText, model), supportingCode) : undefined;
    insertSnippetAtLine({
      editor: editorInst,
      textToInsert,
      ...(snippetTextToInsert ? { snippetTextToInsert, triggerParameterHints: true } : {}),
      targetLine,
      imports,
    });

    try {
      debouncedSync(model.getValue());
    } catch {
      // Ignore sync errors
    }

    return true;
  };

  const handleActionArgsDialogClose = () => {
    setActionArgsDialogOpen(false);
    setPendingActionDrop(null);
  };

  const handleActionArgsDialogConfirm = (filledCode: string, filledInsertText?: string) => {
    let editorToFocus: monaco.editor.IStandaloneCodeEditor | null = null;
    if (pendingActionDrop) {
      const inserted = insertSnippetWithUniquify(
        filledCode,
        pendingActionDrop.targetLine,
        pendingActionDrop.imports,
        filledInsertText,
        pendingActionDrop.supportingCode
      );
      if (inserted && pendingActionDrop.snippetTitle) {
        onSnippetInserted?.(pendingActionDrop.snippetTitle);
      }
      if (inserted) {
        editorToFocus = standaloneEditorRef.current;
      }
    }
    setActionArgsDialogOpen(false);
    setPendingActionDrop(null);
    if (editorToFocus) {
      focusEditorAfterDialogClose(editorToFocus);
    }
  };

  const requestActionArgsDrop = (drop: PendingActionDrop) => {
    setPendingActionDrop(drop);
    setActionArgsDialogOpen(true);
  };

  const requestDeviceSnippetPortDrop = (drop: PendingDeviceSnippetPortDrop & { targetLine: number }) => {
    setPendingDeviceSnippetPortDrop(drop);
    setDeviceSnippetPortDialogOpen(true);
  };

  const handleDeviceSnippetPortDialogClose = () => {
    setDeviceSnippetPortDialogOpen(false);
    setPendingDeviceSnippetPortDrop(null);
  };

  const handleDeviceSnippetPortDialogConfirm = (filledCode: string) => {
    let editorToFocus: monaco.editor.IStandaloneCodeEditor | null = null;
    if (pendingDeviceSnippetPortDrop) {
      const inserted = insertSnippetWithUniquify(filledCode, pendingDeviceSnippetPortDrop.targetLine, pendingDeviceSnippetPortDrop.imports);
      if (inserted && pendingDeviceSnippetPortDrop.snippetTitle) {
        onSnippetInserted?.(pendingDeviceSnippetPortDrop.snippetTitle);
      }
      if (inserted) {
        editorToFocus = standaloneEditorRef.current;
      }
    }
    setDeviceSnippetPortDialogOpen(false);
    setPendingDeviceSnippetPortDrop(null);
    if (editorToFocus) {
      focusEditorAfterDialogClose(editorToFocus);
    }
  };

  const requestDuplicateDriveBaseDialog = () => {
    setDuplicateDriveBaseDialogOpen(true);
  };

  const handleDuplicateDriveBaseDialogClose = () => {
    setDuplicateDriveBaseDialogOpen(false);
  };

  return {
    pathDialogOpen,
    selectedPathName,
    setSelectedPathName,
    pendingPathDrop,
    noPathsDialogOpen,
    handlePathDialogClose,
    handlePathDialogConfirm,
    handleNoPathsDialogClose,
    requestPathDrop,
    requestNoPathsDialog,
    paths: project?.paths || [],
    actionArgsDialogOpen,
    pendingActionDrop,
    pathOptions,
    handleActionArgsDialogClose,
    handleActionArgsDialogConfirm,
    requestActionArgsDrop,
    deviceSnippetPortDialogOpen,
    pendingDeviceSnippetPortDrop,
    handleDeviceSnippetPortDialogClose,
    handleDeviceSnippetPortDialogConfirm,
    requestDeviceSnippetPortDrop,
    duplicateDriveBaseDialogOpen,
    handleDuplicateDriveBaseDialogClose,
    requestDuplicateDriveBaseDialog,
  };
}
